import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'src/hooks/use-debounce';
import RHFAutocomplete from './rhf-autocomplete';

// ----------------------------------------------------------------------
// Reusable RHF Autocomplete that debounces the user's typed input and
// reports it via `onSearch`, so the caller can drive a server-side
// (API) search - e.g. useGetXQuery({ keyword }). Keeps every consumer
// (clients, geographic adjusters, etc.) from re-implementing the same
// debounce + input-change wiring.

export default function RHFAutocompleteSearch({
  onSearch,
  debounceDelay = 300,
  minSearchLength = 0,
  ...other
}) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInputValue = useDebounce(inputValue.trim(), debounceDelay);

  useEffect(() => {
    if (debouncedInputValue.length >= minSearchLength) {
      onSearch?.(debouncedInputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue, minSearchLength]);

  return (
    <RHFAutocomplete
      {...other}
      filterOptions={(options) => options}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'input') {
          setInputValue(newValue);
        }
        // 'clear' = the field's (x) button; 'reset' = MUI clearing the typed
        // text on its own after a selection (multi-select) or on blur -
        // either way the search keyword is no longer "live", so drop it and
        // let the caller's list fall back to its unfiltered/default state.
        if (reason === 'clear' || reason === 'reset') {
          setInputValue('');
        }
      }}
    />
  );
}

RHFAutocompleteSearch.propTypes = {
  onSearch: PropTypes.func,
  debounceDelay: PropTypes.number,
  minSearchLength: PropTypes.number,
};
